//@flow 

import moment from 'moment';
import ko from 'rgs-knockout';

import { initNamespaceStorage } from 'js-storage';

import { ObservableDisableTimer } from 'observable-disable-timer';
import {
	read as momentFromIso,
	write as momentToIso,
} from 'ko-extender-view-moment-from-iso';
import type { TimeoutServiceOptions, ITimeoutService } from '../contracts';

const LAST_SMS_SENT_WHEN = '__phoneCheckingLastSmsSentWhen__';
const CURRENT_SMS_TIMEOUT = '__phoneCheckingCurrentSmsTimeout__';
const MOMENT_BEFORE_INCREMENTING = '__phoneCheckingMomentBeforeIncrementing__';
const SMS_HAS_BEEN_SENT = '__smsHasBeenSent__';

const { localStorage } = initNamespaceStorage('timeout-service');

export default class TimeoutService
	implements ITimeoutService {

	constructor (options: TimeoutServiceOptions) {
		this._options = options;
		/*
		 * isEnabled - на данный момент неактуальное поле, но его наличие обосновано предполагаемой расширяемостью
		*/
		this.isEnabled = options.enabled;

		this._serverTime = options.serverTime;
		/*
		 * Момент последней отправки запроса на подтверждение телефона
		*/
		this.lastSendingMoment = momentFromIso(localStorage.get(LAST_SMS_SENT_WHEN));
		/*
		 * Момент первой отправки запроса на подтверждение телефона до активации прогрессирующего интервала
		*/
		this.momentBeforeIncrementing = localStorage.get(MOMENT_BEFORE_INCREMENTING)
			? momentFromIso(localStorage.get(MOMENT_BEFORE_INCREMENTING))
			: null;

		/*
		 * Число в секундах периода времени, который был задан в качестве времени таймаута
		*/
		this.timeoutPeriod = localStorage.get(CURRENT_SMS_TIMEOUT)
			|| options.base
			|| 0;
		/** 
		 * Флаг для определения остутствия необходимости прирастить интервал(в случае первой отправки)
		*/
		this._hasSmsAlreadyBeenSent = localStorage.get(SMS_HAS_BEEN_SENT) || false;
		
		/*
		 * Число в секундах, на которую каждый раз будет увеличиваться интервал таймаута
		*/
		this.timeoutIncrement = options.increment || 0;
		
		this.timer = new ObservableDisableTimer({
			timeout: [this.timeoutPeriod, 'second'],
		});
		this._applyTimeoutPeriodIfAnyIsPresent();
		/*
		 * Obs для завязки на него шаблона модалки
		*/
		this.isAllowedToSend = ko.computed(() => {
			if (this._isTimeToAutoReset()) {
				this._resetTimeout();
			}
			const timeoutRemaining = this.timer.timeoutRemaining();

			return this._getLeftTimeoutSeconds(timeoutRemaining) === 0;
		});

		this._save();
	}

	/* 
	* Полный сброс прогрессирующего интервала таймаута, разблокировка
	*/
	_resetTimeout = () => {
		this.timeoutPeriod = this._options.base;
		this.lastSendingMoment = null;
		this.momentBeforeIncrementing = null;
		this._hasSmsAlreadyBeenSent = false;
		this._save();
	}

	/* 
	* Активация блокировки таймаутом
	*/
	_disable = () => {
		this.timer.disable({timeout: [this.timeoutPeriod, 'second']});
	}

	/* 
	* Обновление модели и приращение интервала после очередной отправки запроса
	*/
	updateSendMoment = () => {
		this.lastSendingMoment = this._serverTime();
		this._extendTimeout();
		this._hasSmsAlreadyBeenSent = true;
		if (!this.momentBeforeIncrementing) {
			this.momentBeforeIncrementing = this._serverTime();
		}
		this._save();
		this._disable();
	}

	/* 
	* Приращение интервала таймаута
	*/
	_extendTimeout = () => {
		let increment = this.timeoutIncrement;
		/**
		 * Обработка первой отправки, когда должен использоваться базовый таймаут без инкремента
		 */
		if (!this._hasSmsAlreadyBeenSent) {
			increment = 0;
		}

		this.timeoutPeriod += increment;

		this._save();
	}

	/* 
	* Получение числа в секундах, отражающего оставшееся время блокировки таймаутом
	*/
	_getLeftTimeoutSeconds = timeoutRemaining => {
		const remaining = timeoutRemaining
			|| (this.lastSendingMoment
				&& moment(this.lastSendingMoment)
					.add(this.timeoutPeriod, 'seconds').diff(this._serverTime(), 'seconds'));


		if (remaining && remaining > 0) {
			return remaining;
		}

		return 0;
	}

	/* 
	* Проверка необходимости сделать сброс интервала по условию заданного периода полного сброса
	*/
	_isTimeToAutoReset = () => {
		let shouldResetTimeout = false;

		if (this.lastSendingMoment) {
			const now = this._serverTime();
			const { resetTime } = this._options;

			shouldResetTimeout = Math.abs(
				moment(this.momentBeforeIncrementing).diff(now, 'seconds')
			) > resetTime;
		}

		return shouldResetTimeout;
	}

	/* 
	* Проверка на наличие неоконченного таймаута при открытии новой страницы
	*/
	_applyTimeoutPeriodIfAnyIsPresent = () => {
		const now = this._serverTime();
		const lastSentMoment = moment(this.lastSendingMoment);

		const amountOfTimeSinceLastRequest = Math.abs(now.diff(lastSentMoment, 'seconds'));

		if (this.timeoutPeriod > amountOfTimeSinceLastRequest) {
			this.timer.disable({
				timeout: [this.timeoutPeriod - amountOfTimeSinceLastRequest, 'second'],
			});
		}
	}

	/* 
	* Синхронизация с localstorage
	*/
	_save = () => {
		localStorage.set(LAST_SMS_SENT_WHEN, momentToIso(this.lastSendingMoment));
		localStorage.set(CURRENT_SMS_TIMEOUT, this.timeoutPeriod);
		localStorage.set(MOMENT_BEFORE_INCREMENTING, momentToIso(this.momentBeforeIncrementing));
		localStorage.set(SMS_HAS_BEEN_SENT, this._hasSmsAlreadyBeenSent);
	}
}